"use client";

import { useState } from "react";
import {
  Checkbox,
  type Option,
  Panel,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  ExampleButton,
  NoticeList,
  StatementCard,
} from "@/components/tools/publishing-statements/kit";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  type ConsentType,
  type EthicsInputs,
  type ExemptionBasis,
  ethicsStatement,
  type StudyType,
} from "@/lib/tool-kit/publishing/ethics";
import {
  labelledParagraph,
  type RichParagraph,
} from "@/lib/tool-kit/publishing/rich-text";

/** English strings. A translated content file overrides these through `ui`. */
export const ETHICS_STATEMENT_GENERATOR_STRINGS = {
  studyLabel: "What does the study involve?",
  studyHuman: "Human participants or their data",
  studyAnimal: "Animals",
  studyExempt: "Approval not required, or exempt",
  committeeHuman: "Approving committee (IRB or research ethics committee)",
  committeeAnimal: "Approving committee (e.g. IACUC or animal welfare body)",
  committeeExempt: "Committee or office that made the decision",
  committeePlaceholder:
    "e.g. the University of Eastbrook School of Psychology Research Ethics Committee",
  committeeHint: "Its full name, as on the approval letter.",
  referenceHuman: "Approval or reference number",
  referenceAnimal: "Protocol or license number",
  referenceExempt: "Reference number (optional)",
  referencePlaceholder: "e.g. PSY-2024-117",
  dateLabel: "Date of approval (optional)",
  basisLabel: "Decision",
  basisExempt: "The committee found the study exempt",
  basisNotRequired: "Approval was not required",
  exemptionReasonLabel: "Why (optional)",
  exemptionReasonPlaceholder:
    "e.g. it involved only an anonymous survey of adults",
  helsinkiLabel: "Conducted in accordance with the Declaration of Helsinki",
  guidelinesLabel: "Other guidelines or regulations (optional)",
  guidelinesPlaceholder: "e.g. the US Common Rule (45 CFR 46)",
  consentLabel: "Consent",
  consentWrittenOption: "Written",
  consentElectronicOption: "Electronic (online)",
  consentOralOption: "Oral (verbal)",
  consentImpliedOption: "Implied, by completing an anonymous survey",
  consentWaivedOption: "Waived by the committee",
  oralReasonLabel: "Why written consent was not used",
  oralReasonPlaceholder: "e.g. many participants could not read or write",
  oralRecordLabel: "How oral consent was documented",
  oralRecordPlaceholder:
    "e.g. a researcher's signed note, witnessed by a second researcher",
  waiverReasonLabel: "Why consent was waived",
  waiverReasonPlaceholder:
    "e.g. the study used de-identified records collected for routine care",
  minorsLabel: "Some participants were under 18",
  publicationLabel:
    "The article includes identifiable images or details of a person",
  regulationsLabel: "Laws or regulations followed (optional)",
  regulationsPlaceholder: "e.g. EU Directive 2010/63/EU",
  arriveLabel: "Reported following the ARRIVE guidelines 2.0",
  tryExample: "Try an example",
  exampleCommittee:
    "the University of Eastbrook School of Psychology Research Ethics Committee",
  exampleReference: "PSY-2024-117",
  exampleDate: "2024-03-05",
  results: "Your statement",
  heading: "Ethics statement",
  copy: "Copy statement",
  copied: "Copied!",
  approvalLabel: "Ethics approval",
  consentParagraphLabel: "Consent to participate",
  publicationParagraphLabel: "Consent for publication",
  labelEnd: ":",
  noticeCommittee:
    "Name the committee in full. Nature Portfolio asks for the approving committee's name and reference number, and PLOS for the committee's name.",
  noticeReference:
    "Add the approval or protocol number — journals such as Nature Portfolio and PLOS ask for it.",
  noticeOral:
    "For oral consent, PLOS asks why written consent could not be obtained, whether the committee approved oral consent, and how it was documented.",
  noticeExemptBy:
    "Say who decided. Nature Portfolio asks for the details of the committee that granted an exemption.",
  noticePlaceholders: "Replace each bracketed placeholder before you submit.",
  statementLocale: "en",
  approvalHuman: "This study was approved by {committee}{details}.",
  approvalAnimal:
    "All procedures involving animals were approved by {committee}{details}.",
  onDate: " on {date}",
  reference: " (reference number {reference})",
  protocol: " (protocol number {reference})",
  helsinki:
    "The study was conducted in accordance with the Declaration of Helsinki.",
  helsinkiAnd:
    "The study was conducted in accordance with the Declaration of Helsinki and {guidelines}.",
  guidelines: "The study was conducted in accordance with {guidelines}.",
  animalRegulations:
    "All procedures were carried out in accordance with {regulations}.",
  arrive: "The study is reported in accordance with the ARRIVE guidelines 2.0.",
  exemptReviewed:
    "{committee} reviewed this study and determined that it was exempt from further ethical review{because}{reference}.",
  notRequiredBy:
    "Ethical approval was not required for this study{because}, as confirmed by {committee}{reference}.",
  notRequired: "Ethical approval was not required for this study{because}.",
  because: " because {reason}",
  consentWritten:
    "Written informed consent was obtained from all participants.",
  consentElectronic:
    "All participants gave informed consent electronically before taking part.",
  consentOral:
    "Oral informed consent was obtained from all participants because {reason}.",
  consentOralApproved:
    "{committee} approved this procedure, and consent was documented by {record}.",
  consentImplied:
    "Participants gave implied consent by completing the anonymous questionnaire after reading the study information.",
  consentWaived:
    "{committee} waived the requirement for informed consent because {reason}.",
  consentMinors:
    "For participants under 18, informed consent was also obtained from a parent or legal guardian.",
  consentPublication:
    "Written informed consent was obtained from the individuals concerned for the publication of any identifiable images or details in this article.",
  placeholderCommittee: "[committee name]",
  placeholderReason: "[reason]",
  placeholderRecord: "[how consent was recorded]",
} as const;

type Strings = typeof ETHICS_STATEMENT_GENERATOR_STRINGS;

const INITIAL: EthicsInputs = {
  study: "human",
  committee: "",
  reference: "",
  date: "",
  helsinki: true,
  guidelines: "",
  basis: "exempt",
  exemptionReason: "",
  consent: "written",
  oralReason: "",
  oralRecord: "",
  waiverReason: "",
  minors: false,
  publication: false,
  regulations: "",
  arrive: true,
};

const COMMITTEE_LABEL: Record<StudyType, keyof Strings> = {
  human: "committeeHuman",
  animal: "committeeAnimal",
  exempt: "committeeExempt",
};

const REFERENCE_LABEL: Record<StudyType, keyof Strings> = {
  human: "referenceHuman",
  animal: "referenceAnimal",
  exempt: "referenceExempt",
};

function ConsentFields({
  inputs,
  onChange,
  strings,
}: {
  inputs: EthicsInputs;
  onChange: (patch: Partial<EthicsInputs>) => void;
  strings: Strings;
}) {
  const options: Option<ConsentType>[] = [
    { value: "written", label: strings.consentWrittenOption },
    { value: "electronic", label: strings.consentElectronicOption },
    { value: "oral", label: strings.consentOralOption },
    { value: "implied", label: strings.consentImpliedOption },
    { value: "waived", label: strings.consentWaivedOption },
  ];
  return (
    <Panel title={strings.consentLabel}>
      <SelectField<ConsentType>
        id="eth-consent"
        label={strings.consentLabel}
        onChange={(consent) => onChange({ consent })}
        options={options}
        value={inputs.consent}
      />
      {inputs.consent === "oral" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="eth-oral-reason"
            label={strings.oralReasonLabel}
            onChange={(oralReason) => onChange({ oralReason })}
            placeholder={strings.oralReasonPlaceholder}
            value={inputs.oralReason}
          />
          <TextField
            id="eth-oral-record"
            label={strings.oralRecordLabel}
            onChange={(oralRecord) => onChange({ oralRecord })}
            placeholder={strings.oralRecordPlaceholder}
            value={inputs.oralRecord}
          />
        </div>
      ) : null}
      {inputs.consent === "waived" ? (
        <TextField
          id="eth-waiver-reason"
          label={strings.waiverReasonLabel}
          onChange={(waiverReason) => onChange({ waiverReason })}
          placeholder={strings.waiverReasonPlaceholder}
          value={inputs.waiverReason}
        />
      ) : null}
      <div className="space-y-2">
        <Checkbox
          checked={inputs.minors}
          label={strings.minorsLabel}
          onChange={(minors) => onChange({ minors })}
        />
        <Checkbox
          checked={inputs.publication}
          label={strings.publicationLabel}
          onChange={(publication) => onChange({ publication })}
        />
      </div>
    </Panel>
  );
}

function noticesFor(
  inputs: EthicsInputs,
  output: string,
  strings: Strings
): string[] {
  const notes: string[] = [];
  const placeholders = [
    strings.placeholderCommittee,
    strings.placeholderReason,
    strings.placeholderRecord,
  ];
  if (placeholders.some((placeholder) => output.includes(placeholder))) {
    notes.push(strings.noticePlaceholders);
  }
  if (inputs.study !== "exempt" && inputs.committee.trim() === "") {
    notes.push(strings.noticeCommittee);
  }
  if (inputs.study !== "exempt" && inputs.reference.trim() === "") {
    notes.push(strings.noticeReference);
  }
  if (inputs.study === "exempt" && inputs.committee.trim() === "") {
    notes.push(strings.noticeExemptBy);
  }
  if (inputs.study !== "animal" && inputs.consent === "oral") {
    notes.push(strings.noticeOral);
  }
  return notes;
}

export default function EthicsStatementGeneratorWidget() {
  const strings = useToolUi(ETHICS_STATEMENT_GENERATOR_STRINGS);
  const [inputs, setInputs] = useState<EthicsInputs>(INITIAL);
  const patch = (change: Partial<EthicsInputs>) =>
    setInputs((current) => ({ ...current, ...change }));

  const statement = ethicsStatement(inputs, strings, strings.statementLocale);
  const sections: [string, string][] = [
    [strings.approvalLabel, statement.approval],
    [strings.consentParagraphLabel, statement.consent],
    [strings.publicationParagraphLabel, statement.publication],
  ];
  const paragraphs: RichParagraph[] = sections
    .filter(([, text]) => text !== "")
    .map(([label, text]) =>
      labelledParagraph(`${label}${strings.labelEnd}`, text)
    );
  const output = Object.values(statement).join(" ");
  const notes = noticesFor(inputs, output, strings);

  const studyOptions: Option<StudyType>[] = [
    { value: "human", label: strings.studyHuman },
    { value: "animal", label: strings.studyAnimal },
    { value: "exempt", label: strings.studyExempt },
  ];

  function loadExample() {
    setInputs({
      ...INITIAL,
      committee: strings.exampleCommittee,
      reference: strings.exampleReference,
      date: strings.exampleDate,
    });
  }

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.studyLabel}
        name="eth-study"
        onChange={(study) => patch({ study })}
        options={studyOptions}
        value={inputs.study}
      />
      {inputs.study === "exempt" ? (
        <PillGroup<ExemptionBasis>
          label={strings.basisLabel}
          name="eth-basis"
          onChange={(basis) => patch({ basis })}
          options={[
            { value: "exempt", label: strings.basisExempt },
            { value: "notRequired", label: strings.basisNotRequired },
          ]}
          value={inputs.basis}
        />
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.committeeHint}
          id="eth-committee"
          label={strings[COMMITTEE_LABEL[inputs.study]]}
          onChange={(committee) => patch({ committee })}
          placeholder={strings.committeePlaceholder}
          value={inputs.committee}
        />
        <TextField
          id="eth-reference"
          label={strings[REFERENCE_LABEL[inputs.study]]}
          mono
          onChange={(reference) => patch({ reference })}
          placeholder={strings.referencePlaceholder}
          value={inputs.reference}
        />
        {inputs.study === "exempt" ? (
          <TextField
            id="eth-exempt-reason"
            label={strings.exemptionReasonLabel}
            onChange={(exemptionReason) => patch({ exemptionReason })}
            placeholder={strings.exemptionReasonPlaceholder}
            value={inputs.exemptionReason}
          />
        ) : (
          <TextField
            id="eth-date"
            label={strings.dateLabel}
            onChange={(date) => patch({ date })}
            type="date"
            value={inputs.date}
          />
        )}
        {inputs.study === "animal" ? (
          <TextField
            id="eth-regulations"
            label={strings.regulationsLabel}
            onChange={(regulations) => patch({ regulations })}
            placeholder={strings.regulationsPlaceholder}
            value={inputs.regulations}
          />
        ) : (
          <TextField
            id="eth-guidelines"
            label={strings.guidelinesLabel}
            onChange={(guidelines) => patch({ guidelines })}
            placeholder={strings.guidelinesPlaceholder}
            value={inputs.guidelines}
          />
        )}
      </div>
      {inputs.study === "animal" ? (
        <Checkbox
          checked={inputs.arrive}
          label={strings.arriveLabel}
          onChange={(arrive) => patch({ arrive })}
        />
      ) : (
        <>
          <Checkbox
            checked={inputs.helsinki}
            label={strings.helsinkiLabel}
            onChange={(helsinki) => patch({ helsinki })}
          />
          <ConsentFields inputs={inputs} onChange={patch} strings={strings} />
        </>
      )}
      <ExampleButton label={strings.tryExample} onClick={loadExample} />

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <StatementCard
          copiedLabel={strings.copied}
          copyLabel={strings.copy}
          heading={strings.heading}
          paragraphs={paragraphs}
        />
        <NoticeList items={notes} />
      </section>
    </div>
  );
}
